import { defineComponent as _defineComponent } from 'vue'
import {
  Map, Marker,
} from 'maplibre-gl';
import { uniq } from 'underscore';
import {
  computed, onMounted, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

// eslint-disable-next-line
// @ts-ignore
import pinBlue from '~img/sites/region/pin-blue.svg';
import {
  MappedCountedLocation,
  MappedLocation,
  ShopId,
  ShopWall,
} from '~model';
import store from '~store';


export default /*#__PURE__*/_defineComponent({
  __name: 'MapTilerInner',
  props: {
  shopWall: {
    type: Array as () => ShopWall,
    required: true,
  },
},
  setup(__props) {

const parentProperties = __props

/* eslint-disable no-underscore-dangle */


const route = useRoute();
const router = useRouter();

const map = store.state.region?.map as Map;
const markers = store.state.region?.markers as Marker[];

function getVisibleMarkers(): Marker[] {
  const bounds = map.getBounds();

  const min = bounds._sw;
  const max = bounds._ne;
  const minLat = min.lat;
  const minLng = min.lng;
  const maxLat = max.lat;
  const maxLng = max.lng;

  const visibleMarkers = markers.filter((marker) => {
    const coords = marker.getLngLat();
    const { lat, lng } = coords;

    return lat > minLat &&
      lat < maxLat &&
      lng > minLng &&
      lng < maxLng;
  });

  return visibleMarkers;
}

function getVisibleMarkersShopIds(): ShopId[] {
  return uniq(
    getVisibleMarkers()
      // eslint-disable-next-line
      // @ts-ignore
      .map((marker) => marker.shopIds || marker.shopId)
      .flat(),
  );
}

const zoomLevel = ref(1);

const zoomLevelRounded = computed(() => Math.round(zoomLevel.value));

const zoomCloseThreshold = computed(() => {
  switch (zoomLevelRounded.value) {
    case 5:
      return 0.35;

    case 6:
      return 0.25;

    case 7:
      return 0.125;

    case 8:
      return 0.1;

    default:
      return 0;
  }
});

const mappedShops = computed(
  () => parentProperties
    .shopWall
    .map(
      (shop) => ({
        geoLocations: shop
          ?.geoLocations
          ?.map((geoLocation) => ({
            ...geoLocation,
            shopId: shop.id,
          })),
      }),
    ),
);

const mappedLocations = computed(
  () => mappedShops
    .value
    .map((shop) => shop.geoLocations)
    .flat() as MappedLocation[],
);

const closeLocations = computed(
  () => {
    if (!zoomCloseThreshold.value) {
      return mappedLocations
        .value as MappedCountedLocation[];
    }

    return mappedLocations
      .value
      .reduce((
        result: MappedCountedLocation[],
        entry,
      ) => {
        const existingEntry = result
          .find((resultEntry: MappedCountedLocation) => {
            const latitudeDiff = Math.abs(entry.latitude - resultEntry.latitude);
            const longitudeDiff = Math.abs(entry.longitude - resultEntry.longitude);

            return latitudeDiff <= zoomCloseThreshold.value
            && longitudeDiff <= zoomCloseThreshold.value;
          }) as MappedCountedLocation;

        if (existingEntry) {
          existingEntry.latitude = (existingEntry.latitude * existingEntry.count + entry.latitude) / (existingEntry.count + 1);
          existingEntry.longitude = (existingEntry.longitude * existingEntry.count + entry.longitude) / (existingEntry.count + 1);
          existingEntry.count += 1;
          if (entry.shopId) {
            existingEntry.shopIds.push(entry.shopId);
          }
          existingEntry.shopId = undefined;
        } else {
          result.push({
            latitude: entry.latitude,
            longitude: entry.longitude,
            shopId: entry.shopId,
            shopIds: [],
            count: 1,
          });
        }

        return result;
      }, []);
  },
);

function locationClicked(
  location: MappedCountedLocation,
): void {
  if (location.count > 1) {
    map.easeTo({
      center: [
        location.longitude,
        location.latitude,
      ],
      zoom: zoomLevel.value + 1,
      duration: 600,
    });
  } else if (route.name === 'shops') {
    const { href } = router.resolve(
      {
        name: 'shopDetails',
        params: {
          id: String(location.shopId),
        },
      },
    );

    router.push(href);
  } else {
    store.state.region.shopModal = true;
    store.dispatch('fetchShopDetails', location.shopId);
  }
}

function setMarkers(): void {
  markers.forEach((marker) => marker.remove());
  store.state.region.markers = [];

  closeLocations
    .value
    .forEach((location) => {
      const { count, shopId, shopIds } = location;
      const size = 36 + count / 2;
      const element = document.createElement('div');

      element.innerHTML = String(count || '');
      element.className = `shop-marker count-${count} shop-id-${shopId}`;
      element.style.backgroundImage = `url(${pinBlue})`;
      element.style.width = `${size}px`;
      element.style.height = `${size}px`;
      element.addEventListener('click', () => {
        locationClicked(location);
      });

      const marker = new Marker({ element })
        .setLngLat([
          location.longitude,
          location.latitude,
        ])
        .addTo(map);

      // eslint-disable-next-line
      // @ts-ignore
      marker.shopId = shopId;

      // eslint-disable-next-line
      // @ts-ignore
      marker.shopIds = shopIds;

      markers.push(marker);
    });
}

function zoomEnd(): void {
  zoomLevel.value = map.getZoom();
  setMarkers();
}

function moveEnd(): void {
  if (zoomLevel.value > 5) {
    store.state.filter.shopIds = getVisibleMarkersShopIds();
  } else {
    store.state.filter.shopIds = [];
  }
}

onMounted(() => {
  store.state.region.markers = [];

  map.on('zoomend', zoomEnd);
  map.on('moveend', moveEnd);

  setMarkers();
  moveEnd();
  zoomEnd();
});

return { __sfc: true,parentProperties, route, router, map, markers, getVisibleMarkers, getVisibleMarkersShopIds, zoomLevel, zoomLevelRounded, zoomCloseThreshold, mappedShops, mappedLocations, closeLocations, locationClicked, setMarkers, zoomEnd, moveEnd }
}

})